import React, { useState, useCallback, useRef } from 'react';
import { useAllNotifications, Notification } from './api/useAllNotifications';
import { Env, getAdminApiUrl, ENV_COLORS, Spinner, ErrorBanner } from '../shared';

const PAGE_SIZE = 50;
const GLOBAL_CAP = 10_000;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    sent: 'bg-emerald-950/50 border-emerald-800/50 text-emerald-400',
    failed: 'bg-red-950/50 border-red-800/50 text-red-400',
    pending: 'bg-yellow-950/50 border-yellow-800/50 text-yellow-400',
  };
  const cls = styles[status] ?? 'bg-gray-800 border-gray-700 text-gray-400';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-lg text-xs font-medium capitalize ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status}
    </span>
  );
}

function BusinessAvatar({ name, logo }: { name: string; logo: string | null }) {
  return (
    <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-700">
      {logo
        ? <img src={logo} alt={name} className="w-full h-full object-cover" />
        : <span className="text-xs font-bold text-gray-400">{name.charAt(0).toUpperCase()}</span>}
    </div>
  );
}

// ── Main Tab ──────────────────────────────────────────────────────────────────

export function BusinessPushNotificationsTab(): React.ReactElement {
  const [selectedEnv, setSelectedEnv] = useState<Env>('dev');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const offset = page * PAGE_SIZE;

  const { notifications, total, loading, error } = useAllNotifications({
    env: selectedEnv,
    search: debouncedSearch,
    limit: PAGE_SIZE,
    offset,
  });

  const totalPages = Math.ceil(Math.min(total, GLOBAL_CAP) / PAGE_SIZE);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  }, []);

  const handleEnvChange = useCallback((env: Env) => {
    setSelectedEnv(env);
    setPage(0);
    setSearch('');
    setDebouncedSearch('');
  }, []);

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Filter by business name…"
            className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
          />
        </div>

        {!loading && total > 0 && (
          <span className="text-sm text-gray-500">
            {total >= GLOBAL_CAP ? `${GLOBAL_CAP.toLocaleString()}+ total` : `${total.toLocaleString()} total`}
          </span>
        )}

        {/* Env switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map(env => {
            const isConfigured = Boolean(getAdminApiUrl(env));
            const isActive = selectedEnv === env;
            return (
              <button
                key={env}
                onClick={() => isConfigured && handleEnvChange(env)}
                disabled={!isConfigured}
                title={isConfigured ? undefined : 'Not configured'}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[env].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {env}
              </button>
            );
          })}
        </div>

        {selectedEnv === 'prod' && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}
      </div>

      {error && <ErrorBanner message={error} />}

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="hidden md:grid grid-cols-[2fr_1.5fr_3fr_6rem_6rem_8rem] gap-4 px-5 py-3 border-b border-gray-800">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Business</p>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</p>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Message</p>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Subscribers</p>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</p>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</p>
        </div>

        {loading && (
          <div className="py-16 text-center flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500">Loading notifications…</p>
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-gray-500 text-sm">
              {debouncedSearch ? 'No notifications match your search.' : 'No notifications found.'}
            </p>
          </div>
        )}

        {!loading && notifications.map((n, i) => (
          <NotificationRow
            key={n.id}
            notification={n}
            isLast={i === notifications.length - 1}
          />
        ))}
      </div>

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-xs text-gray-500">
            Showing {offset + 1}–{Math.min(offset + notifications.length, total)} of{' '}
            {total >= GLOBAL_CAP ? `${GLOBAL_CAP.toLocaleString()}+` : total.toLocaleString()} notifications
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                ← Prev
              </button>
              <span className="text-xs text-gray-500 tabular-nums">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

function NotificationRow({ notification: n, isLast }: { notification: Notification; isLast: boolean }) {
  return (
    <div className={`flex flex-col md:grid md:grid-cols-[2fr_1.5fr_3fr_6rem_6rem_8rem] gap-4 px-5 py-4 hover:bg-gray-800/50 transition-colors ${!isLast ? 'border-b border-gray-800' : ''}`}>
      {/* Business */}
      <div className="flex items-center gap-2.5 min-w-0">
        <BusinessAvatar name={n.businessName} logo={n.businessLogo} />
        <p className="text-sm font-medium text-white truncate">{n.businessName}</p>
      </div>

      {/* Title */}
      <p className="text-sm text-gray-200 truncate self-center">{n.title}</p>

      {/* Body */}
      <p className="text-sm text-gray-400 truncate self-center">{n.body}</p>

      {/* Subscribers */}
      <p className="text-sm text-gray-300 tabular-nums self-center text-right">{n.subscriberCount.toLocaleString()}</p>

      {/* Status */}
      <div className="self-center">
        <StatusBadge status={n.status} />
      </div>

      {/* Date */}
      <p className="text-xs text-gray-500 self-center whitespace-nowrap">{formatDate(n.createdAt)}</p>
    </div>
  );
}
